import * as React from 'react';
import { Dispatch } from 'redux';
import axios, { AxiosError } from 'axios';
import { SafActionsMenu, receiveAxiosError, userHasAnyRole } from '@konicaminolta/common-react';
import { Alert, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import Select from 'react-select';
import { User } from 'oidc-client';
import { EnumValues } from 'enum-values';
import { PowerBiObjectType } from '../../../types/Common/PowerBiObjectType';
import { RoleMemberType, RoleMemberTypeDisplayName } from '../../../types/RecordAccessControl/Enums/RoleMemberType';
import { RecordAccessControl } from '../../../types/RecordAccessControl/RecordAccessControl';
import { CreateRecordAccessControlEntry } from '../../../types/RecordAccessControl/CreateRecordAccessControlEntry';
import { AccessControlRequired } from '../../../types/RecordAccessControl/AccessControlRequired';
import { PowerBiAccessControlEntryType, PowerBiAccessControlEntryTypeDisplayName } from '../../../types/RecordAccessControl/Enums/PowerBiAccessControlEntryType';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { ROLE_FULL_ADMINISTRATOR, ROLE_POWER_USER } from '../../../appRoles';
import { IPowerBiReportViewerObject } from '../../../types/Common/IPowerBiReportViewerObject';
import RoleMemberAsyncTypeahead from './RoleMemberAsyncTypeahead';
import { RoleMember } from '../../../types/RecordAccessControl/RoleMember';
import DeleteAccessControlEntryModal from './DeleteAccessControlEntryModal';

interface RoleMemberTypeDescriptor {
    value: RoleMemberType;
}
interface PowerBiAccessControlEntryTypeDescriptor {
    value: PowerBiAccessControlEntryType;
}

type RecordAccessControlTableProps = {
    dispatch: Dispatch;
    record: IPowerBiReportViewerObject;
    recordType: PowerBiObjectType;
    user: User;
}

interface RecordAccessControlTableState {
    data: RecordAccessControl[];
    recordAccessControl: RecordAccessControl | undefined;
    isLoading: boolean;
    isLoaded: boolean;
    isAccessControlListRequiredChecked: boolean;
    hasPermissionsToMembers: boolean;
    showModal: boolean;
    showModalError: boolean;
    isTypeAheadValid: boolean;
    isTypeAheadInvalid: boolean;
    validated: boolean;
    createRecordAccessControl: CreateRecordAccessControlEntry;
    accessControlEntryTypeDropdownOptions: {value: PowerBiAccessControlEntryType}[];
    showDeleteModal: boolean;
    selectedAccessControlEntryDropdownValue: PowerBiAccessControlEntryTypeDescriptor;
    selectedMemberTypeDropdownValue: RoleMemberTypeDescriptor;

}

class RecordAccessControlTable extends React.Component<RecordAccessControlTableProps, RecordAccessControlTableState> {
    public constructor(props: RecordAccessControlTableProps) {
        super(props);
        this.state = {
            data: [],
            recordAccessControl: undefined,
            isLoading: true,
            isLoaded: false,
            hasPermissionsToMembers: true,
            isAccessControlListRequiredChecked : props.record.isAccessControlEntryRequired ?? false,
            showDeleteModal: false,
            showModal: false,
            showModalError: false,
            isTypeAheadValid: false,
            isTypeAheadInvalid: false,
            validated: false,
            selectedAccessControlEntryDropdownValue: { value: PowerBiAccessControlEntryType.User},
            selectedMemberTypeDropdownValue: { value: RoleMemberType.MyKonicaMinoltaPerson },
            createRecordAccessControl: {
                personEntryUuid: undefined,
                jobCodeId: undefined,
                accessControlEntryType: PowerBiAccessControlEntryType.User,
                roleMemberType: RoleMemberType.MyKonicaMinoltaPerson,
            },
            accessControlEntryTypeDropdownOptions: EnumValues.getValues(PowerBiAccessControlEntryType).filter(x => x !== PowerBiAccessControlEntryType.JobCode).map(function(value) { return { value: value as PowerBiAccessControlEntryType }}),
        };
    }

    public componentDidMount() {
        this.ensureDataFetched();
    }

    public componentDidUpdate(prevProps: Readonly<RecordAccessControlTableProps>, prevState: Readonly<RecordAccessControlTableState>, snapshot?: any): void {
        // If the Access Control Type dropdown list no longer contains the drop downs selected value, change the value
        if (this.getAccessControlEntryTypeDropdownOptions().some(item => item.value == this.state.selectedAccessControlEntryDropdownValue.value) == false) {
            this.setAccessControlEntryTypeDescriptor(this.getAccessControlEntryTypeDropdownOptions()[0]);
        }
    }

    private ensureDataFetched() {
        this.getRecordAccessControlRecords();
        if (this.state.isLoading && this.state.isLoaded) {
            this.setState({isLoading: false});
        }
    }

    private handleRefreshRecordAccessControlList() {
        this.setState({isLoading: true});
        this.getRecordAccessControlRecords();
    };

    private getRecordAccessControlRecords() {

        let url = `./api/reports/${this.props.record.id}/members`;
        if (this.props.recordType == PowerBiObjectType.Dashboard) {
            url = `./api/dashboards/${this.props.record.id}/members`;
        }

        axios
            .get<RecordAccessControl[]>(url)
            .then((response) => {
                const { data } = response;
                this.setState({ data: data, isLoading: false, isLoaded: true });
            })
            .catch((e: AxiosError) => {
                if (e.response && e.response.status == 401) {
                    this.setState({hasPermissionsToMembers: false});
                }
                else if (e.response && e.response.status !== 404) {
                    this.props.dispatch(receiveAxiosError({ error: e }));
                }
                this.setState({ isLoading: false, isLoaded: true });
            });
    }

    private updateRecordAccessControlRequired(isRequired: boolean) {
        let dto = {} as AccessControlRequired;
        dto.isAccessControlRequired = isRequired;

        let url = `./api/reports/${this.props.record.id}/accessControlRequired`;
        if (this.props.recordType == PowerBiObjectType.Dashboard) {
            url = `./api/dashboards/${this.props.record.id}/accessControlRequired`;
        }

        axios
        .patch<RecordAccessControl>(url, dto, { timeout: 30000 })
        .then(() => {
            this.handleRefreshRecordAccessControlList();
        })
        .catch((e: AxiosError) => {
            this.props.dispatch(receiveAxiosError({ error: e }));
        });
    }

    private setRoleMemberType(memberType: RoleMemberTypeDescriptor) {
        this.setState({selectedMemberTypeDropdownValue: memberType});
        this.setState({
            createRecordAccessControl: {
                ...this.state.createRecordAccessControl,
                roleMemberType: memberType.value,
            }
        });
    }

    private setAccessControlEntryTypeDescriptor(accessControlEntryTypeDescriptor: PowerBiAccessControlEntryTypeDescriptor) {
        this.setState({selectedAccessControlEntryDropdownValue: accessControlEntryTypeDescriptor})
        this.setState({
            createRecordAccessControl: {
                ...this.state.createRecordAccessControl,
                accessControlEntryType: accessControlEntryTypeDescriptor.value,
            }
        });
    }

    private getAccessControlEntryTypeDropdownOptions() {
       if (this.state.createRecordAccessControl?.roleMemberType == RoleMemberType.MyKonicaMinoltaJobCode) {
            return EnumValues.getValues(PowerBiAccessControlEntryType).filter(x => x == PowerBiAccessControlEntryType.JobCode).map(function(value) { return { value: value as PowerBiAccessControlEntryType }});
        }

        return EnumValues.getValues(PowerBiAccessControlEntryType).filter(x => x !== PowerBiAccessControlEntryType.JobCode).map(function(value) { return { value: value as PowerBiAccessControlEntryType }});
    }

//#region Member Search

    private roleMemberSelectionCallback = (roleMember: RoleMember | undefined) => {
        this.setTypeAheadValidity(roleMember);
    }

    private setTypeAheadValidity(roleMember: RoleMember | undefined) {
        if(roleMember === undefined){
            this.setState(
                {
                    isTypeAheadValid: false,
                    isTypeAheadInvalid: true,
                    createRecordAccessControl: {
                        ...this.state.createRecordAccessControl,
                        personEntryUuid: undefined,
                        jobCodeId: undefined,
                        roleMemberType: RoleMemberType.MyKonicaMinoltaPerson,
                    }
                }
            );
        }
        else {
            let roleMemberTypeValue : RoleMemberType = RoleMemberType[roleMember.type.toString() as keyof typeof RoleMemberType];
            this.setState(
                {
                    isTypeAheadValid: true,
                    isTypeAheadInvalid: false,
                    createRecordAccessControl: {
                        ...this.state.createRecordAccessControl,
                        personEntryUuid: roleMemberTypeValue === RoleMemberType.MyKonicaMinoltaPerson ? roleMember.id : undefined,
                        jobCodeId: roleMemberTypeValue === RoleMemberType.MyKonicaMinoltaJobCode ? roleMember.id : undefined,
                        accessControlEntryType: roleMemberTypeValue === RoleMemberType.MyKonicaMinoltaJobCode ? PowerBiAccessControlEntryType.JobCode : PowerBiAccessControlEntryType.User, //FIXME TO BE A DROP DOWN
                        roleMemberType: roleMemberTypeValue,
                    }
                }
            );
        }
    }

    private handleOpen(){
        this.setState({
            showModal: true
        });
    }
    
    private handleClose(){
        this.setState({
            showModal: false
        });
        this.handleRefreshRecordAccessControlList();
    }
    
//#endregion

    private handleSubmitModal = (e: React.FormEvent<HTMLInputElement>) => {
        e.preventDefault();
        e.stopPropagation();

        const form = e.currentTarget;

        if(form.checkValidity()){
            if(this.state.createRecordAccessControl.jobCodeId === undefined && this.state.createRecordAccessControl.personEntryUuid === undefined){
                console.log("showing validation error messagE");
                this.setState({showModalError: true});
            }
            else {
                this.submitMember();
                // Reset the model
                this.setState({
                    showModalError: false,
                    createRecordAccessControl: {
                        ...this.state.createRecordAccessControl,
                        personEntryUuid: undefined,
                        jobCodeId: undefined,
                    }
                });
            }
        }

        this.setState({validated: true});
    }

    private submitMember() {
        // Build DTO to post:
        let addMemberDto = this.state.createRecordAccessControl;
        addMemberDto.accessControlEntryType = this.state.selectedAccessControlEntryDropdownValue.value;


        let url = `./api/reports/${this.props.record.id}/members`;
        if (this.props.recordType == PowerBiObjectType.Dashboard) {
            url = `./api/dashboards/${this.props.record.id}/members`;
        }

        axios
        .post<CreateRecordAccessControlEntry>(url, addMemberDto, { timeout: 30000 })
        .then(() => {
            this.setState({showModalError: false});
            this.handleClose();
        })
        .catch((e: AxiosError) => {
            let errorMessage = e.response?.data ? e.response?.data : e.message;
            this.setState({showModalError: true});
        });
    }

    private handleDeleteAccessControlEntryModalOpen(recordAccessControl: RecordAccessControl) {
        this.setState({recordAccessControl: recordAccessControl});
        this.setState({showDeleteModal: true});
    }

    private handleDeleteAccessControlEntryModalOkButton() {
        if(userHasAnyRole(this.props.user, [ROLE_FULL_ADMINISTRATOR, ROLE_POWER_USER])) {
            this.handleCloseDeleteAccessControlEntryModal();
        }
    }

    // Close and clear ReportAlias handle
    private handleCloseDeleteAccessControlEntryModal() {
        this.setState({showDeleteModal: false});
        this.setState({recordAccessControl: undefined});

        this.handleRefreshRecordAccessControlList();
    }

    render() {

        const handleIsAccessControlListRequiredCheckboxClick = (event: React.ChangeEvent<HTMLInputElement>) => {
            this.setState({isAccessControlListRequiredChecked: event.target.checked});
            this.updateRecordAccessControlRequired(event.target.checked);
        }

        const NoDataComponent: React.FunctionComponent = (): React.ReactElement => {
            if (!this.state.isLoading && !this.state.hasPermissionsToMembers) {
                return (
                    <div className="d-flex justify-content-center mt-3" data-cy="bootstrap-table-loaded">
                        <p className="text-center">Insufficient permissions to view access control list.</p>
                    </div>
                );
            }
            if (!this.state.isLoading) {
                return (
                    <div className="d-flex justify-content-center mt-3" data-cy="bootstrap-table-loaded">
                        <p className="text-center">No permissions found.</p>
                    </div>
                );
            }
            else {
                return (
                    <div className="d-flex justify-content-center mt-3">
                        <Spinner animation="border" variant="primary" role="status">
                            <span className="sr-only">Loading...</span>
                        </Spinner>
                    </div>
                );
            }
        };

        const { SearchBar } = Search;

        const columns = [
            {
                isDummyField: true,
                dataField: 'accessControlEntryId',
                text: 'Type',
                sort: true,
                headerClasses: 'col-md-1',
                formatter: (cell: any, row: RecordAccessControl) => {
                    if (row.jobCodeId !== null) {
                        return "Job Code"
                    }
                    return "User"
                }
            },
            {
                dataField: 'accessControlEntryDisplayName',
                text: 'Name',
                sort: true,
            },
            {
                dataField: 'accessControlEntryType',
                text: 'Access Type',
                sort: true,
                formatter: (cell: any, row: RecordAccessControl) => {
                    let accessControlEntryTypeValue : PowerBiAccessControlEntryType = PowerBiAccessControlEntryType[row.accessControlEntryType.toString() as keyof typeof PowerBiAccessControlEntryType];
                    return PowerBiAccessControlEntryTypeDisplayName[accessControlEntryTypeValue];
                }
            },
            {
                dataField: '',
                text: '',
                sort: false,
                headerClasses: 'col-md-1',
                formatter: (cell: any, row: RecordAccessControl) => {
                    if(userHasAnyRole(this.props.user, [ROLE_FULL_ADMINISTRATOR, ROLE_POWER_USER])){
                        return (<SafActionsMenu
                            menuItems={[
                                /*{
                                    icon: faPenToSquare,
                                    label: 'Edit',
                                    onClick: () => {
                                        this.handleOpenEditAliasModal(row);
                                    },
                                },*/
                                {
                                    icon: faTrash,
                                    label: 'Delete',
                                    onClick: () => {
                                        this.handleDeleteAccessControlEntryModalOpen(row);
                                    }
                                },
                            ]}
                            id={row.accessControlEntryId} />
                        );
                    }              
                }
            }
        ];

        return (
            <React.Fragment>
                <Row>
                    <Col>
                        <Card className="panel">
                            <Card.Header>
                                <Row>
                                    <Col md={6}>
                                        <h3>Permissions</h3>
                                    </Col>
                                    <Col md={6} className='text-end'>
                                        <Button 
                                            variant={userHasAnyRole(this.props.user, [ROLE_FULL_ADMINISTRATOR, ROLE_POWER_USER]) ? 'primary' : 'secondary' }
                                            className='ms-3' 
                                            onClick={() => this.handleOpen()}
                                            disabled={!userHasAnyRole(this.props.user, [ROLE_FULL_ADMINISTRATOR, ROLE_POWER_USER])}
                                        >
                                            Add Permission
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col>
                                    <div className={"form-inline"}>
                                    <label className="checkbox-padding">Requires Access Control List:</label>
                                    <input type="checkbox"
                                        id={"requiresAccessListCheck-"+this.props.record.id}
                                        className={"selection-input-4 clickable"}
                                        checked={this.state.isAccessControlListRequiredChecked}
                                        onChange={handleIsAccessControlListRequiredCheckboxClick}
                                        disabled={!userHasAnyRole(this.props.user, [ROLE_FULL_ADMINISTRATOR, ROLE_POWER_USER])}
                                    />
                                    </div>
                                    <small>When checked visibility and access to this {this.props.recordType == PowerBiObjectType.Report ? "report" : "dashboard"} is limited to Administrators and the access list below</small>
                                    </Col>
                                </Row>
                                <Row>
                                <ToolkitProvider
                                    keyField="accessControlEntryId"
                                    search
                                    data={this.state.data}
                                    columns={columns}
                                >
                                    {
                                        props => (
                                            <React.Fragment>
                                                <Row>
                                                    <Col>
                                                        <SearchBar {...props.searchProps} />
                                                    </Col>
                                                </Row>
                                                <BootstrapTable
                                                    {...props.baseProps}
                                                    id="recordAccessControlTable"
                                                    classes='clickable'
                                                    striped
                                                    hover
                                                    bordered={false}
                                                    defaultSorted={[{ dataField: 'accessControlEntryId', order: 'asc' }]}
                                                    pagination={paginationFactory({ sizePerPage: 10 })}
                                                    noDataIndication={() => <NoDataComponent />}
                                                />
                                            </React.Fragment>
                                        )
                                    }
                                </ToolkitProvider>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                <Modal size="lg" show={this.state.showModal} onHide={() => this.handleClose()}>
                    <Form 
                        noValidate 
                        validated={this.state.validated} 
                        onSubmit={(e: React.FormEvent<any>) => this.handleSubmitModal(e)}
                    >
                    <Modal.Header closeButton>
                        <Modal.Title>Add Member</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {(this.state.showModalError) &&
                        <Alert variant="danger" className="text-center" onClose={() => this.handleClose()} dismissible>
                            <strong>All fields required.</strong>
                        </Alert>
                        }
                        <Form.Group as={Row}>
                            <Form.Label column sm="3">Member Type</Form.Label>
                            <Col sm="9">
                                <Select<RoleMemberTypeDescriptor>
                                    isSearchable={false}
                                    isClearable={false}
                                    options={EnumValues.getValues(RoleMemberType).map(function(value) { return { value: value as RoleMemberType }})}
                                    getOptionLabel={(option) => {
                                        return RoleMemberTypeDisplayName[option.value];
                                    }} 
                                    getOptionValue={(option) => {
                                        return option.value.toString();
                                    }} 
                                    placeholder="Select a role member type..."
                                    onChange={(selectedOption) => {
                                        if (Array.isArray(selectedOption)) {
                                            throw new Error("Unexpected type passed to ReactSelect onChange handler");
                                        }
                                        if(selectedOption !== undefined && selectedOption !== null) {
                                            this.setRoleMemberType(selectedOption);
                                        }
                                    }}
                                    value={this.state.selectedMemberTypeDropdownValue}
                                />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} controlId="request-person" className="async-typeahead">
                            <Form.Label column sm="3">Member Name</Form.Label>
                            <Col sm="9">
                                <RoleMemberAsyncTypeahead
                                    roleMemberSelectionCallback={this.roleMemberSelectionCallback}
                                    memberType={this.state.createRecordAccessControl.roleMemberType}
                                    isValid={this.state.isTypeAheadValid}
                                    isInvalid={this.state.isTypeAheadInvalid}
                                />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} controlId="access-control-entry-type">
                            <Form.Label column sm="3">Access Control Type</Form.Label>
                            <Col sm="9">
                                <Select<PowerBiAccessControlEntryTypeDescriptor>
                                    isSearchable={false}
                                    isClearable={false}
                                    options={this.getAccessControlEntryTypeDropdownOptions()}
                                    getOptionLabel={(option) => {
                                        return PowerBiAccessControlEntryTypeDisplayName[option.value];
                                    }} 
                                    getOptionValue={(option) => {
                                        return option.value.toString();
                                    }} 
                                    placeholder="Select an access control type..."
                                    onChange={(selectedOption) => {
                                        if (Array.isArray(selectedOption)) {
                                            throw new Error("Unexpected type passed to ReactSelect onChange handler");
                                        }
                                        if(selectedOption !== undefined && selectedOption !== null) {
                                            this.setAccessControlEntryTypeDescriptor(selectedOption);
                                        }
                                    }}
                                    value={this.state.selectedAccessControlEntryDropdownValue}
                                />
                            </Col>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" type="submit">
                            Add Member
                        </Button>
                    </Modal.Footer>
                    </Form>
                </Modal>
                <DeleteAccessControlEntryModal
                  dispatch={this.props.dispatch}
                  accessControlEntry={this.state.recordAccessControl!}
                  recordId={this.props.record.id}
                  recordType={this.props.recordType}
                  showModal={this.state.showDeleteModal}
                  okButton={() => this.handleDeleteAccessControlEntryModalOkButton()}
                  hideModal={() => this.handleCloseDeleteAccessControlEntryModal()}
                  modalBody={`Delete Access Control Entry: ${this.state.recordAccessControl?.accessControlEntryDisplayName}`}
                 />
            </React.Fragment>
        );
    }
}

export default RecordAccessControlTable;